/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import debounce from "lodash.debounce";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import {
  AllContactsTotalAtom,
  ContactFilterAtom,
  IsDefaultStatusAtom,
} from "../../atoms/ContactFilterAtom";
import {
  ClearFilterAtom,
  ContactResetTagSelect,
  FilterCounterAtom,
  SelectedLabelAtom,
  ShowSidebarFilter,
} from "../../atoms/ContactFilterAtoms";
import { ContactFiltersAtom } from "../../atoms/ContactFiltersAtom";
import { ContactsAtom } from "../../atoms/ContactsAtom";
import { ContactsPerPageAtom } from "../../atoms/ContactsPerPageAtom";
import { LabelAtom } from "../../atoms/LabelAtom";
import { SelectedContactsAtom } from "../../atoms/SelectedContactsAtom";
import { UserAtom } from "../../atoms/UserAtom";
import { ICategoryType } from "../../generated/Api";
import useClickOutside from "../../hook/useClickOutside";
import { FilterByEmailAtom } from "../../pages/contacts";
import { ITag } from "../../types/TagTypes";
import {
  localStorageGetItem,
  localStorageRemoveItem,
  localStorageSetItem,
} from "../../utils/LocalStorage";
import IconBerserkerMail from "../Icon";
import Loading from "../Loading";
import AddContact from "../Modal/container/AddContact";
import ImportContact from "../Modal/container/ImportContact";
import Warning from "../Modal/container/Warning";
import { useModal } from "../Modal/Modal";
import SearchInput from "../SearchInput";
import StyledButton from "../StyledButton";
import ActionsPopOver from "./ActionsPopOver";
import { allStatusArray } from "./Sidebar/FilterByStatus";
import { Circle } from "./Sidebar/SidebarStyles";

const Container = styled.div`
  width: 100%;
  min-height: 83px;
  border-bottom: 1px solid #cccccc;
  display: flex;
  justify-content: space-between;
  padding: 15px;
  align-items: center;
  padding-left: 25px;
  background-color: #ffffff;
  z-index: 10;
`;

const Infos = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

const Buttons = styled.div`
  height: 43px;
  display: flex;
  width: fit-content;
  align-items: center;
  justify-content: flex-end;
`;

const Title = styled.span`
  color: #222222;
  font-weight: 600;
`;

interface ButtonFilterProps {
  selected?: boolean;
}

const StyledButtonWithIcon = styled(StyledButton)<ButtonFilterProps>`
  justify-content: space-between;
  align-items: center;
  height: 40px;
  width: 100%;

  ${(props) =>
    props.selected &&
    css`
      border-color: #fb972e;
      padding-right: 12px;
    `}

  > div {
    margin-right: 7px;
  }

  .close {
    margin-left: 12px;
    margin-right: 0px;
    padding: 2px;
  }
`;

const TitlePopover = styled.div`
  font-size: 13px;
  color: #888888;
  margin-bottom: 5px;
  margin-left: 17px;
  padding-top: 10px;
`;

const AddContactPopover = styled.div`
  position: absolute;
  background: white;
  z-index: 1002;
  top: 48px;
  right: 0;
  height: fit-content;
  width: 240px;
  overflow: auto;
  border-radius: 5px;
  box-shadow: 0px 3px 20px #00000026;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;
  display: flex;
  flex-direction: column;
  padding: 10px 0px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: #2f2f2f;
  cursor: pointer;

  span {
    margin-left: 7px;
  }

  &:hover {
    background-color: #fb972e;
    color: #ffffff;
  }
`;

const marginRight15 = css`
  margin-right: 15px;
`;

const addButton = css`
  width: 136px;
  justify-content: space-evenly;
  padding: 10px;
  height: 43px;
  margin-right: 10px;
`;

const ClearFilterWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-right: 0px !important;
  margin-left: 12px;

  > div {
    opacity: 0.7;
  }

  > div:hover {
    opacity: 1;
  }
`;

export const CUSTOM_TAG = (n: number, system?: boolean): ITag => ({
  name: `+${n} more`,
  _id: "-1",
  category: "user",
  userUid: "",
  type: system ? ICategoryType.System : ICategoryType.User,
  createdAt: new Date(),
  categoryId: "",
});

export const Input = styled(motion.input)`
  height: 35px;
  border: none;
  color: "#2F2F2F";
  background-color: "#FFFFFF";
  padding-left: 40px;

  border-radius: 5px;

  &::placeholder {
    font-size: 14px;
    letter-spacing: 0.16px;
    font-family: "Source Sans Pro", sans-serif !important;
    color: "#B1BBC6";
  }

  font-size: 14px;
  font-family: "Source Sans Pro", sans-serif !important;

  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`;

const ShowingContactsMessage = styled.div`
  font-weight: 600;
`;

export default function Header(): JSX.Element {
  const modal = useModal();
  const [contactFilters, setContactFilters] =
    useRecoilState(ContactFiltersAtom);
  const resetContactFilters = useResetRecoilState(ContactFiltersAtom);
  const setContactFilter = useSetRecoilState(ContactFilterAtom);
  const total = useRecoilValue(AllContactsTotalAtom);
  const setFilterByEmail = useSetRecoilState(FilterByEmailAtom);
  const cachedSearchInput =
    process.browser &&
    localStorage &&
    (localStorageGetItem("SEARCH_INPUT") as string);
  const [searchInput, setSearchInput] = useState("");
  const [debounceSearch, setDebounceSearch] = useState("");

  const filteredContacts = useRecoilValue(ContactsAtom);
  const [openDropView, setOpenDropView] = useState(false);
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const router = useRouter();
  const contactsOnPage = useRecoilValue(ContactsPerPageAtom);
  const [loadingBlock, setLoadingBlock] = useState(false);
  const selectedContacts = useRecoilValue(SelectedContactsAtom);
  // const debouncedSearch = useDebounce(searchInput, 800);
  const filterCounter = useRecoilValue(FilterCounterAtom);
  const [showSideBar, setShowSideBar] = useRecoilState(ShowSidebarFilter);
  const [totalCount, setTotalCount] = useState(0);
  const { search } = router.query;
  const resetFilterCounter = useResetRecoilState(FilterCounterAtom);
  const setIsDefaultStatus = useSetRecoilState(IsDefaultStatusAtom);
  const setShouldClear = useSetRecoilState(ClearFilterAtom);
  const resetTagSelect = useSetRecoilState(ContactResetTagSelect);
  const labels = useRecoilValue(LabelAtom);
  const user = useRecoilValue(UserAtom);
  const setSelectedLabel = useSetRecoilState(SelectedLabelAtom);
  const isFirstRender = useRef(true);

  useEffect(() => {
    const total = Object.values(filterCounter).reduce(
      (previousValue, currentValue) => previousValue + currentValue
    );

    setTotalCount(total);
  }, [filterCounter]);

  useEffect(() => {
    const initialSearch = search || cachedSearchInput;

    if (!initialSearch) return;
    handleChange(initialSearch as string);
    setFilterByEmail(initialSearch as string);
  }, [search]);

  useEffect(() => {
    if (
      contactFilters?.search &&
      (searchInput === "" || debounceSearch === "")
    ) {
      handleChange(contactFilters.search);
    }
  }, [contactFilters]);

  useEffect(() => {
    if (isFirstRender.current && !debounceSearch) {
      isFirstRender.current = false;
      return;
    }

    if (!debounceSearch) {
      setContactFilters((oldFilter) => ({ ...oldFilter, search: undefined }));
    } else {
      setContactFilters((oldFilter) => ({
        ...oldFilter,
        search: debounceSearch as string,
      }));
    }
  }, [debounceSearch]);

  function useDebounce(callback, delay) {
    const debouncedFn = useCallback(
      debounce((...args) => callback(...args), delay),
      [delay] // will recreate if delay changes
    );
    return debouncedFn;
  }

  const debouncedSave = useDebounce(
    (nextValue) => setDebounceSearch(nextValue),
    250
  );

  const handleChange = (value) => {
    setSearchInput(value);
    debouncedSave(value);
  };

  useEffect(() => {
    try {
      localStorageSetItem("SEARCH_INPUT", searchInput as string);
    } catch (error) {
      console.log(error);
    }
  }, [searchInput]);

  function clearAll() {
    resetFilterCounter();
    resetContactFilters();
    setIsDefaultStatus(true);
    setShouldClear(true);
    setSelectedLabel([]);
    resetTagSelect((t) => !t);
    setContactFilter(null);
  }

  return (
    <Container>
      <Infos>
        {/* NEED TO UPDATE THIS, AS WE'RE SAVING USER'S PREFERENCE ON PAGINATION */}
        {!totalCount && (
          <ShowingContactsMessage>
            {filteredContacts.length < 1
              ? "Showing 0 contacts"
              : filteredContacts.length > 1
              ? `Showing ${filteredContacts.length} of ${total} contacts`
              : `Showing ${filteredContacts.length} of ${total} contact`}
          </ShowingContactsMessage>
        )}
        {!!totalCount && (
          <>
            <Title>
              {total ? (
                <>
                  Showing {contactsOnPage} of{" "}
                  {total > 1
                    ? `${total} filtered contacts`
                    : `${total} filtered contact`}
                </>
              ) : (
                "Showing 0 contact"
              )}
            </Title>
          </>
        )}
      </Infos>

      <Buttons>
        <SearchInput
          value={searchInput}
          handleChange={(inputValue) => {
            isFirstRender.current = false;
            handleChange(inputValue);
            !!inputValue && localStorageRemoveItem("SEARCH_INPUT");
          }}
          placeholder="Search for contacts"
          css={css`
            width: 220px;
            margin: 15px;
            height: 43px;
          `}
        />
        {loadingBlock && (
          <div
            style={{
              height: "100vh",
              width: "100vw",
              position: "fixed",
              zIndex: 9999999999,
              top: "0",
              left: "0",
              background: "rgb(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Loading />
          </div>
        )}
        {selectedContacts.length > 1 && (
          <div css={marginRight15}>
            <ActionsPopOver setLoadingBlock={setLoadingBlock} />
          </div>
        )}

        <StyledButtonWithIcon
          inverted
          css={css`
            width: fit-content;
            height: 43px;
            margin-right: 15px;
            white-space: nowrap;

            transition: right 0.3s ease-in-out;
            transition: opacity 1s ease-in-out;

            border-color: ${totalCount && !showSideBar ? "#fb972e" : "#ddddd"};
          `}
          onClick={() => setShowSideBar((f) => !f)}
        >
          {totalCount && !showSideBar ? (
            <Circle isVisible={true}>{totalCount}</Circle>
          ) : (
            <IconBerserkerMail name="filter" />
          )}
          Filter
          {!!totalCount && !showSideBar && (
            <ClearFilterWrapper
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
            >
              <IconBerserkerMail
                name="close-modal"
                color="#bbb"
                width={12}
                height={12}
              />
            </ClearFilterWrapper>
          )}
        </StyledButtonWithIcon>

        <div
          css={css`
            display: flex;
            position: relative;
          `}
        >
          <StyledButtonWithIcon
            onClick={() => {
              setOpenDropView(!openDropView);
            }}
            css={addButton}
          >
            <IconBerserkerMail
              name="plus"
              width={11}
              height={11}
              color="#fff"
            />
            Add Contact
          </StyledButtonWithIcon>

          {openDropView && (
            <AddContactPopover ref={wrapperRef}>
              <TitlePopover>ADD CONTACT</TitlePopover>

              <Row
                onClick={() => {
                  modal.pushModal(<AddContact />, "Add new contact");
                  setOpenDropView(false);
                }}
              >
                <span>Create New Contact</span>
              </Row>

              <Row
                onClick={() => {
                  user.allowImport
                    ? modal.pushModal(<ImportContact />, "")
                    : modal.pushModal(
                        <Warning
                          title="Contact Imports are Temporarily Restricted"
                          description="We have had some issues with clients uploading very old lists that are full of Spam Traps and Bots over the last several weeks. So until we have rectified this situation, we are temporarily suspending contact imports. We can still get your contacts imported for you, just please send a download link to your CSV file to us on the help desk at help@berserkermail.com and any instructions on field mapping or tagging and we can do this for you in the short term."
                          buttonsName={["OK"]}
                          icon="/icons/block-user.png"
                          onCancelClicked={() => modal.popModal()}
                          onOkClicked={() => modal.popModal()}
                        />
                      );
                  setOpenDropView(false);
                }}
              >
                <span>Batch Import/Update</span>
              </Row>
            </AddContactPopover>
          )}
        </div>
      </Buttons>
    </Container>
  );
}
